
  import {
    defineComponent,
    computed,
    nextTick,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { Actions } from '@/store/enums/StoreEnums';
  import {
    DeeplinkApiResult,
    DeeplinkDestination,
    DeeplinkMember,
    DeeplinkNotificationPayload,
    DeeplinkSendResult,
  } from '@/store/modules/Notifications/DeeplinkNotificationsModule';
  import {
    buildDeeplink,
    DATE_RANGE_DESTINATIONS,
    DEEPLINK_BASE,
    destinationParams,
    DeeplinkParam,
    ParamSpec,
    useDeeplinkLookups,
  } from '@/core/helpers/deeplink';

  // TODO: switch to 'deeplink-notifications' once roles have it.
  const PERMISSION_RESOURCE = 'users';
  const MAX_MEMBERS = 500;
  const TITLE_MAX = 255;
  const BODY_MAX = 1000;

  export default defineComponent({
    name: 'deeplink-notification',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const canSend = computed(() => can('create', PERMISSION_RESOURCE));

      const formRef = ref();
      const resultRef = ref<HTMLElement | null>(null);
      const forbidden = ref(false);

      const defaultForm = () => ({
        destination: '',
        lang: 'ar',
        titleAr: '',
        titleEn: '',
        bodyAr: '',
        bodyEn: '',
        imageUrl: '',
        audience: 'members' as 'members' | 'all',
      });
      const form = reactive(defaultForm());
      const params = ref<Record<string, unknown>>({});
      const advanced = ref(false);
      const rawDeeplink = ref('');
      const selectedMembers = ref<Array<DeeplinkMember>>([]);
      const memberIds = computed(() => selectedMembers.value.map((m) => m.id));

      /* Destinations */
      const destinations = ref<Array<DeeplinkDestination>>([]);
      const destinationsLoading = ref(false);
      const destinationsError = ref('');

      const loadDestinations = async () => {
        destinationsLoading.value = true;
        destinationsError.value = '';
        const res: DeeplinkApiResult<Array<DeeplinkDestination>> =
          await store.dispatch(Actions.GET_DEEPLINK_DESTINATIONS);
        destinationsLoading.value = false;
        if (res.status === 403) {
          forbidden.value = true;
        } else if (!res.data) {
          destinationsError.value =
            res.message || translate('deeplinkLoadDestinationsFailed');
        } else {
          destinations.value = res.data;
        }
      };

      const screenLabel = (item: DeeplinkDestination) => {
        const key = `deeplinkScreen_${item.destination}`;
        return te(key) ? t(key) : item.screen;
      };

      const selectedDestination = computed(() =>
        destinations.value.find((d) => d.destination === form.destination)
      );

      const currentParams = computed(() =>
        destinationParams(selectedDestination.value)
      );

      const fieldName = (key: string) => `param_${key}`;

      const paramLabel = (key: string) => {
        const specific = `deeplinkParam_${form.destination}_${key}`;
        if (te(specific)) return t(specific);
        const generic = `deeplinkParam_${key}`;
        return te(generic) ? t(generic) : key;
      };

      /* Lookups for select inputs */
      const { isLookupLoading, isWaitingOnParent, paramOptions } =
        useDeeplinkLookups(store, translate, currentParams, params);

      const selectPlaceholder = (spec: ParamSpec, key: string) =>
        spec.dependsOn && isWaitingOnParent(spec)
          ? t('deeplinkSelectFirst', { field: paramLabel(spec.dependsOn) })
          : translate(key);

      watch(
        () => form.destination,
        () => {
          // Clear the previous type's fields and start the new ones empty.
          const next: Record<string, unknown> = {};
          currentParams.value.forEach((p) => {
            next[p.key] = p.spec.input === 'multiselect' ? [] : '';
          });
          params.value = next;
        }
      );

      /* Deeplink */
      const deeplink = computed(() =>
        advanced.value
          ? rawDeeplink.value.trim()
          : buildDeeplink(
              form.destination,
              currentParams.value,
              params.value,
              form.lang
            )
      );

      const copied = ref(false);
      const copyDeeplink = async () => {
        if (!deeplink.value) return;
        try {
          await navigator.clipboard.writeText(deeplink.value);
        } catch (e) {
          const input = document.createElement('textarea');
          input.value = deeplink.value;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        copied.value = true;
        setTimeout(() => (copied.value = false), 1500);
      };

      /* Message */
      const messageFields = [
        {
          name: 'titleAr',
          label: 'deeplinkTitleAr',
          max: TITLE_MAX,
          rtl: true,
        },
        { name: 'titleEn', label: 'deeplinkTitleEn', max: TITLE_MAX },
        {
          name: 'bodyAr',
          label: 'deeplinkBodyAr',
          max: BODY_MAX,
          rtl: true,
          multiline: true,
        },
        {
          name: 'bodyEn',
          label: 'deeplinkBodyEn',
          max: BODY_MAX,
          multiline: true,
        },
      ];

      const previews = computed(() => [
        {
          lang: 'ar',
          dir: 'rtl',
          label: 'deeplinkPreviewArabic',
          title: form.titleAr,
          body: form.bodyAr,
        },
        {
          lang: 'en',
          dir: 'ltr',
          label: 'deeplinkPreviewEnglish',
          title: form.titleEn,
          body: form.bodyEn,
        },
      ]);

      const previewImage = computed(() =>
        /^https:\/\/\S+$/.test(form.imageUrl.trim())
          ? form.imageUrl.trim()
          : '/media/logos/logo-square-dark.jpg'
      );

      /* Members picker */
      const memberQuery = ref('');
      const memberResults = ref<Array<DeeplinkMember>>([]);
      const membersLoading = ref(false);
      let memberTimer: ReturnType<typeof setTimeout> | undefined;
      let memberRequest = 0;

      const memberFilter = (query: string) => {
        if (query.includes('@')) return { email: query };
        if (/^\+?[\d\s]+$/.test(query)) {
          return { phone: query.replace(/\s/g, '') };
        }
        return { name: query };
      };

      watch(memberQuery, (value) => {
        if (memberTimer) clearTimeout(memberTimer);
        const query = value.trim();
        if (query.length < 2) {
          memberResults.value = [];
          membersLoading.value = false;
          return;
        }
        membersLoading.value = true;
        memberTimer = setTimeout(async () => {
          const request = ++memberRequest;
          const res = await store.dispatch(
            Actions.SEARCH_DEEPLINK_MEMBERS,
            memberFilter(query)
          );
          // Ignore responses that arrive after a newer search started.
          if (request !== memberRequest) return;
          memberResults.value = res.data || [];
          membersLoading.value = false;
        }, 300);
      });

      const isSelected = (member: DeeplinkMember) =>
        selectedMembers.value.some((m) => m.id === member.id);

      const addMember = (member: DeeplinkMember) => {
        if (isSelected(member) || selectedMembers.value.length >= MAX_MEMBERS) {
          return;
        }
        selectedMembers.value = [...selectedMembers.value, member];
      };

      const removeMember = (member: DeeplinkMember) => {
        selectedMembers.value = selectedMembers.value.filter(
          (m) => m.id !== member.id
        );
      };

      /* Validation */
      const required = () => translate('deeplinkFieldRequired');

      const paramRule = (param: DeeplinkParam) => {
        const { input } = param.spec;
        let rule;
        if (input === 'multiselect') {
          rule = Yup.array().nullable();
          return param.required ? rule.min(1, required) : rule;
        }
        if (input === 'number') {
          rule = Yup.string().matches(/^[1-9]\d*$/, {
            message: () => translate('deeplinkWholeNumber'),
            excludeEmptyString: true,
          });
        } else if (input === 'hash' || input === 'auction') {
          rule = Yup.string().matches(/^\S+$/, {
            message: () => translate('deeplinkNoSpaces'),
            excludeEmptyString: true,
          });
        } else {
          rule = Yup.string();
        }
        rule = rule.nullable().trim();
        if (
          DATE_RANGE_DESTINATIONS.includes(form.destination) &&
          param.key === 'dateTo'
        ) {
          rule = rule.test(
            'date-order',
            () => translate('deeplinkDateOrder'),
            function (this: Yup.TestContext, value) {
              const from = this.parent[fieldName('dateFrom')];
              return !value || !from || from <= value;
            }
          );
        }
        return param.required ? rule.required(required) : rule;
      };

      const maxChars = (max: number) => () => t('deeplinkMaxChars', { max });

      const notificationSchema = computed(() => {
        const shape: Record<string, Yup.AnySchema> = {
          titleAr: Yup.string()
            .trim()
            .required(required)
            .max(TITLE_MAX, maxChars(TITLE_MAX)),
          titleEn: Yup.string()
            .trim()
            .required(required)
            .max(TITLE_MAX, maxChars(TITLE_MAX)),
          bodyAr: Yup.string()
            .trim()
            .required(required)
            .max(BODY_MAX, maxChars(BODY_MAX)),
          bodyEn: Yup.string()
            .trim()
            .required(required)
            .max(BODY_MAX, maxChars(BODY_MAX)),
          imageUrl: Yup.string()
            .trim()
            .matches(/^https:\/\/\S+$/, {
              message: () => translate('deeplinkHttpsOnly'),
              excludeEmptyString: true,
            }),
          audience: Yup.string().oneOf(['members', 'all']).required(required),
        };
        if (advanced.value) {
          shape.rawDeeplink = Yup.string()
            .trim()
            .required(required)
            .matches(/^https:\/\/\S+$/, () => translate('deeplinkLinkInvalid'));
        } else {
          shape.destination = Yup.string().required(required);
          shape.lang = Yup.string().oneOf(['ar', 'en']).required(required);
          currentParams.value.forEach((param) => {
            shape[fieldName(param.key)] = paramRule(param);
          });
        }
        if (form.audience === 'members') {
          shape.memberIds = Yup.array()
            .min(1, () => translate('deeplinkPickUser'))
            .max(MAX_MEMBERS, () => translate('deeplinkTooManyUsers'));
        }
        return Yup.object().shape(shape);
      });

      /* Send */
      const sending = ref(false);
      const result = ref<{ variant: string; lines: Array<string> } | null>(
        null
      );

      const escapeHtml = (value: string) =>
        value.replace(
          /[&<>"']/g,
          (c) =>
            ({
              '&': '&amp;',
              '<': '&lt;',
              '>': '&gt;',
              '"': '&quot;',
              "'": '&#39;',
            }[c] as string)
        );

      const confirmSend = async () => {
        if (form.audience === 'all') {
          const screen = advanced.value
            ? translate('deeplinkCustomLink')
            : selectedDestination.value
            ? screenLabel(selectedDestination.value)
            : '';
          const answer = await Swal.fire({
            title: translate('deeplinkConfirmAllTitle'),
            icon: 'warning',
            html: `
              <div class="text-start">
                <div class="mb-2"><b>${escapeHtml(
                  translate('deeplinkConfirmScreen')
                )}:</b> ${escapeHtml(screen)}</div>
                <div class="mb-4 text-break" dir="ltr"><code>${escapeHtml(
                  deeplink.value
                )}</code></div>
                <div class="text-danger">${escapeHtml(
                  translate('deeplinkAllWarning')
                )}</div>
              </div>`,
            input: 'text',
            inputPlaceholder: translate('deeplinkConfirmTypeSend'),
            inputValidator: (value) =>
              value === 'SEND'
                ? undefined
                : translate('deeplinkConfirmTypeSendError'),
            showCancelButton: true,
            buttonsStyling: false,
            confirmButtonText: translate('deeplinkSend'),
            cancelButtonText: translate('cancel'),
            customClass: {
              confirmButton: 'btn fw-bold btn-danger',
              cancelButton: 'btn btn-light',
            },
          });
          return answer.value === 'SEND';
        }
        const answer = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'question',
          text: t('deeplinkConfirmMembers', {
            count: selectedMembers.value.length,
          }),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('deeplinkSend'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-primary',
            cancelButton: 'btn btn-light',
          },
        });
        return !!answer.value;
      };

      const showError = (status?: number, message?: string) => {
        let text = message || translate('deeplinkErrorGeneric');
        if (status === 409) text = translate('deeplinkErrorConflict');
        if (status === 403) text = translate('deeplinkNoPermission');
        Swal.fire({
          text,
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: translate('tryAgainExcl'),
          customClass: {
            confirmButton: 'btn fw-bold btn-light-danger',
          },
        });
      };

      const describeResult = (data: DeeplinkSendResult) => {
        const total = Number(data.recipients) || 0;
        if (total === 0) {
          return {
            variant: 'warning',
            lines: [translate('deeplinkResultNone')],
          };
        }
        if (data.status === 'queued') {
          return {
            variant: 'info',
            lines: [t('deeplinkResultQueued', { total })],
          };
        }
        const failed = Number(data.failed) || 0;
        const sent = data.sent ?? total - failed;
        const lines = [t('deeplinkResultSent', { sent, total })];
        if (failed > 0) lines.push(t('deeplinkResultFailed', { failed }));
        return { variant: failed > 0 ? 'warning' : 'success', lines };
      };

      const onSubmit = async () => {
        if (sending.value) return;
        if (!(await confirmSend())) return;

        const payload: DeeplinkNotificationPayload = {
          deeplink: deeplink.value,
          titleAr: form.titleAr.trim(),
          titleEn: form.titleEn.trim(),
          bodyAr: form.bodyAr.trim(),
          bodyEn: form.bodyEn.trim(),
          audience: form.audience,
        };
        if (form.audience === 'members') payload.memberIds = memberIds.value;
        if (form.imageUrl.trim()) payload.imageUrl = form.imageUrl.trim();

        sending.value = true;
        result.value = null;
        const res: DeeplinkApiResult<DeeplinkSendResult> = await store.dispatch(
          Actions.SEND_DEEPLINK_NOTIFICATION,
          payload
        );
        sending.value = false;

        if (!res.data) {
          showError(res.status, res.message);
          return;
        }
        result.value = describeResult(res.data);
        await nextTick();
        resultRef.value?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      };

      const resetAll = () => {
        Object.assign(form, defaultForm());
        params.value = {};
        advanced.value = false;
        rawDeeplink.value = '';
        selectedMembers.value = [];
        memberQuery.value = '';
        result.value = null;
        formRef.value?.resetForm({
          values: { ...defaultForm(), rawDeeplink: '', memberIds: [] },
        });
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('sendNotification'), [
          translate('Notification'),
        ]);
      });

      if (canSend.value) loadDestinations();
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        t,
        translate,
        canSend,
        forbidden,
        formRef,
        resultRef,
        DEEPLINK_BASE,
        MAX_MEMBERS,
        form,
        params,
        advanced,
        rawDeeplink,
        destinations,
        destinationsLoading,
        destinationsError,
        loadDestinations,
        screenLabel,
        selectedDestination,
        currentParams,
        fieldName,
        paramLabel,
        paramOptions,
        isLookupLoading,
        isWaitingOnParent,
        selectPlaceholder,
        deeplink,
        copied,
        copyDeeplink,
        messageFields,
        previews,
        previewImage,
        memberQuery,
        memberResults,
        membersLoading,
        selectedMembers,
        memberIds,
        isSelected,
        addMember,
        removeMember,
        notificationSchema,
        sending,
        result,
        onSubmit,
        resetAll,
      };
    },
  });
